import { useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Trans, useTranslation } from 'react-i18next'
import { debounce, uniqBy } from 'lodash-es'
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import InputGroup from 'react-bootstrap/InputGroup'
import Overlay from 'react-bootstrap/Overlay'
import Tooltip from 'react-bootstrap/Tooltip'
import Screen from 'components/layout/Screen'
import Btn from 'components/buttons/Btn'
import EmptyMessage from 'components/EmptyMessage'
import OrdersGroup from 'components/CollapsibleList/OrdersGroup'
import API from 'state/API'
import { getLoading } from 'state/Request'
import { useApiSearch, searchApiOrders, useLocalSearch } from 'state/Orders'
import './styles.scss'

const MiniSpinner = () => useSelector(getLoading(API.RESOURCES.ORDERS))? <Icon icon="spinner" pulse className="text-muted" /> : null

//TODO: check usage of useTransition / useDeferredValue to improve the UX of loading local and remote results
export default function SearchPage() {
	const [query, setQuery]    = useState('')
	const [period, setPeriod]  = useState(30)
	const [showTip, toggleTip] = useState(false)

	const { t }       = useTranslation()
	const tipRef      = useRef()
	const dispatch    = useDispatch()
	const localSearch = useLocalSearch(query, period)
	const apiSearch   = useApiSearch()
	const results     = !query? [] : uniqBy(apiSearch.concat(localSearch), 'id')

	const doSearch = debounce((queryValue, periodValue) => {
		if (query != queryValue) {
			setQuery(queryValue)
		}
		if (period != periodValue) {
			setPeriod(periodValue)
		}
		if (queryValue.length >= 1) {
			dispatch(searchApiOrders(queryValue, periodValue))
		}
	}, 700) //probably needs finer tuning of this time, to avoid taking long to fire a query, while avoiding many queries (that may overlap and leave the UI with an outdated result)... maybe try to cancel older queries before firing new ones?

	return <Screen back title={t("Orders search")} corner={<MiniSpinner/>}>
			<Form className="p-full-page">
				<Form.Group as={Form.Row} controlId="query">
					<Form.Label column xs={2} className="text-right">{t('Term')}</Form.Label>
					<Col>
						<InputGroup>
							<Form.Control type="search" autoFocus onChange={e => doSearch(e.target.value, period)} defaultValue={query}/>
							<InputGroup.Append>
								<Btn icon="question-circle" variant="light" ref={tipRef} onClick={() => toggleTip(!showTip)}/>
								<Overlay placement="bottom" target={tipRef.current} show={showTip}
								         rootClose onHide={() => toggleTip(false)}>
									<Tooltip id="term-help">
										<Trans>Search for an <b>order #</b>, <b>customer name</b> or <b>phone</b>. Use at least 3
											characters for a complete search.</Trans>
									</Tooltip>
								</Overlay>
							</InputGroup.Append>
						</InputGroup>
					</Col>
				</Form.Group>

				<Form.Group as={Form.Row} controlId="period">
					<Form.Label column xs={2} className="text-right">{t('Period')}</Form.Label>
					<Col>
						<Form.Control as="select" onChange={e => doSearch(query, e.target.value)} defaultValue={period}>
							<option value="7">{t('last {{count}} day', { count: 7 })}</option>
							<option value="15">{t('last {{count}} day', { count: 15 })}</option>
							<option value="30">{t('last {{count}} day', { count: 30 })}</option>
							<option value="60">{t('last {{count}} day', { count: 60 })}</option>
							<option value="90">{t('last {{count}} day', { count: 90 })}</option>
							<option value="180">{t('last {{count}} month', { count: 6 })}</option>
							{process.env.NODE_ENV == 'development' && <>
								<option value="365">{t('last {{count}} month', { count: 12 })}</option>
								<option value="730">{t('last {{count}} month', { count: 24 })}</option>
							</>}
						</Form.Control>
					</Col>
				</Form.Group>
			</Form>

		<Screen.Content>
			{results.length?
				<OrdersGroup orders={results} openByDefault noHighlight/>
			:
				<EmptyMessage icon="search" resource={API.RESOURCES.ORDERS} msg={t("Search in all your orders by number, customer name or phone.")}/>
			}
		</Screen.Content>
	</Screen>
}
